var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import Application from '../Application';
import EventEmitter from './EventEmitter';
// How many times to retry a failed load before giving up
const MAX_RETRIES = 3;
function sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
}
export default class Resources extends EventEmitter {
    constructor(sources) {
        super();
        this.sources = sources;
        this.items = { texture: {}, cubeTexture: {}, gltfModel: {}, audio: {} };
        this.toLoad = this.sources.length;
        this.loaded = 0;
        this.application = new Application();
        this.loading = this.application.loading;
        this.setLoaders();
        this.startLoading();
    }
    setLoaders() {
        // --- CHANGED: attach a DRACOLoader to the GLTFLoader.
        // Draco-compressed .glb files are 60–90% smaller, meaning far less RAM
        // is needed to decode and upload the mesh data to the GPU.
        // (You can compress your .glb files with: npx gltf-pipeline -i model.glb -o model.glb --draco.compressionLevel 7)
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
        dracoLoader.preload();
        const gltfLoader = new GLTFLoader();
        gltfLoader.setDRACOLoader(dracoLoader);
        this.loaders = {
            gltfLoader,
            textureLoader: new THREE.TextureLoader(),
            cubeTextureLoader: new THREE.CubeTextureLoader(),
            audioLoader: new THREE.AudioLoader(),
        };
    }
    startLoading() {
        for (const source of this.sources) {
            if (source.type === 'gltfModel') {
                this.loadWithRetry(source);
            }
            else if (source.type === 'texture') {
                this.loaders.textureLoader.load(source.path, (file) => {
                    file.encoding = THREE.sRGBEncoding;
                    this.sourceLoaded(source, file);
                }, undefined, 
                // --- CHANGED: texture load errors now fall through gracefully
                (err) => {
                    console.error(`[Resources] Texture failed: ${source.path}`, err);
                    // Use a 1×1 magenta fallback so the scene still renders
                    const fallback = new THREE.DataTexture(new Uint8Array([255, 0, 255, 255]), 1, 1);
                    fallback.needsUpdate = true;
                    this.sourceLoaded(source, fallback);
                });
            }
            else if (source.type === 'cubeTexture') {
                this.loaders.cubeTextureLoader.load(source.path, (file) => {
                    this.sourceLoaded(source, file);
                });
            }
            else if (source.type === 'audio') {
                this.loaders.audioLoader.load(source.path, (buffer) => {
                    this.sourceLoaded(source, buffer);
                });
            }
        }
    }
    // --- NEW: wraps gltfLoader.load with automatic retry + exponential back-off.
    // Previously a single network hiccup or out-of-memory spike during decode
    // would silently drop the model and leave the scene empty.
    loadWithRetry(source, attempt = 1) {
        return __awaiter(this, void 0, void 0, function* () {
            this.loaders.gltfLoader.load(source.path, (file) => {
                this.sourceLoaded(source, file);
            }, undefined, (err) => __awaiter(this, void 0, void 0, function* () {
                console.warn(`[Resources] GLTF load failed (attempt ${attempt}/${MAX_RETRIES}): ${source.path}`, err);
                if (attempt < MAX_RETRIES) {
                    // Exponential back-off: 500ms → 1000ms → 2000ms
                    yield sleep(500 * Math.pow(2, attempt - 1));
                    this.loadWithRetry(source, attempt + 1);
                }
                else {
                    console.error(`[Resources] Giving up on ${source.path} after ${MAX_RETRIES} attempts.`);
                    // Still call sourceLoaded so the loading screen doesn't freeze.
                    // The world code that uses this model will need to null-check.
                    this.sourceLoaded(source, null);
                }
            }));
        });
    }
    sourceLoaded(source, file) {
        this.items[source.type][source.name] = file;
        this.loaded++;
        this.loading.trigger('loadedSource', [
            source.name,
            this.loaded,
            this.toLoad,
        ]);
        if (this.loaded === this.toLoad) {
            this.trigger('ready');
        }
    }
}
